import { PartnerMediaThumbnail } from '@/components/checklist/PartnerMediaThumbnail';
import { isStorageMediaReference } from '@/utils/media/partnerStorageReference';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { databaseService } from '@/services/databaseService';
import APP_CONSTANTS from '@/config/constants';
import theme from '@/config/theme';
import { AdminLogger } from '@/utils/logger';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Image } from 'react-native';

interface ChecklistItem {
  id: string;
  data_criacao: number;
  status: string;
  templateName: string;
  userName: string;
  sequencial_codigo?: string | null;
  sequencial_numero?: number | null;
}

interface Props {
  data: ChecklistItem[];
  loading: boolean;
  totalCount: number;
  hasMore: boolean;
  onLoadMore?: () => void;
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  selectAll: (ids: string[]) => void;
  openRows?: string[];
  onOpenRowsChange?: (rows: string[]) => void;
  refreshToken?: number;
}

// Utilitário para alternar cor das linhas (zebra)
const getRowBackground = (index: number) =>
  index % 2 === 0 ? theme.colors.background.primary : theme.colors.background.tertiary;

// Mapeia status para label legível
const statusLabel = (s: string) => {
  switch (s) {
    case 'in_progress':
      return 'Em andamento';
    case 'done':
      return 'Concluído';
    case 'pending':
      return 'Pendente';
    default:
      return s;
  }
};

// Cor de fundo para cada status
const statusColor = (s: string) => {
  switch (s) {
    case 'in_progress':
      return theme.colors.secondary.default; // laranja
    case 'done':
      return theme.colors.util.success; // verde
    case 'pending':
      return theme.colors.util.warning; // amarelo
    default:
      return theme.colors.border.dark;
  }
};

// Estilo para texto destacado nos resultados da busca
const highlightStyle: React.CSSProperties = {
  background: theme.colors.primary.light,
  borderRadius: 4,
  padding: '0 2px',
};

const getSequentialLabel = (row: ChecklistItem): string => {
  if (row.sequencial_codigo && row.sequencial_codigo.trim().length > 0) {
    return row.sequencial_codigo.trim();
  }
  if (typeof row.sequencial_numero === 'number' && Number.isFinite(row.sequencial_numero)) {
    return `Checklist #${row.sequencial_numero}`;
  }
  return 'Aguardando numeração';
};

const IMAGE_FIELD_TYPES = ['foto', 'assinatura', 'marcação', 'photo', 'signature', 'mark'];

const getLocalImageTableName = (value: string) => {
  if (value.startsWith('checklist-photos/')) {
    return APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME;
  }
  if (value.startsWith('checklist-signatures/')) {
    return APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME;
  }
  if (value.startsWith('checklist-marcacoes/')) {
    return APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME;
  }
  return null;
};

const resolveLocalImageDataUrl = (base64: string, value: string) => {
  const isPng = value.toLowerCase().endsWith('.png');
  const mimeType = isPng ? 'image/png' : 'image/jpeg';
  return `data:${mimeType};base64,${base64}`;
};

export default function ChecklistsTableWeb({
  data,
  loading,
  totalCount,
  hasMore,
  onLoadMore,
  selectedIds,
  toggleSelect,
  selectAll,
  openRows: openRowsProp,
  onOpenRowsChange,
  refreshToken,
}: Props) {
  const log = AdminLogger.Table;
  const router = useRouter();
  const [search, setSearch] = useState('');
  const [openRowsState, setOpenRowsState] = useState<string[]>([]);
  type DetailItem = {
    campo: string;
    valor: string;
    tipo: string;
    thumb?: string;
    grupo?: string;
    ordem?: number;
  };

  const [detailsCache, setDetailsCache] = useState<Record<string, DetailItem[]>>({});

  // ===== Helpers para busca e destaque =====
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);

  const highlightTerm = (text: string): React.ReactNode => {
    if (!search) return text;
    const regex = new RegExp(`(${terms.join('|')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} style={highlightStyle}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const makeThumb = (url: string): string => isStorageMediaReference(url) ? url : `${url}${url.includes('?') ? '&' : '?'}width=80`;

  /**
   * Carrega (ou atualiza) os detalhes de um checklist.
   * @param rowId id do checklist
   * @param includeThumbs quando true gera as miniaturas das imagens; se já existirem não gera novamente
   */
  const fetchDetails = async (
    rowId: string,
    includeThumbs: boolean,
    options?: { force?: boolean }
  ) => {
    // Se já estiver em cache
    if (detailsCache[rowId] && !options?.force) {
      // Se precisarmos adicionar thumbs e ainda não existirem, atualiza em memória
      if (includeThumbs) {
        const needsUpdate = detailsCache[rowId].some(
          (d) => IMAGE_FIELD_TYPES.includes(d.tipo) && !d.thumb,
        );
        if (!needsUpdate) return; // nada para fazer

        const updated = detailsCache[rowId].map((d) => {
          if (IMAGE_FIELD_TYPES.includes(d.tipo) && !d.thumb) {
            const newThumb = makeThumb(d.valor);
            log.info('Thumb gerado (cache)', { campo: d.campo, newThumb });
            return { ...d, thumb: newThumb };
          }
          return d;
        });
        setDetailsCache((prev) => ({ ...prev, [rowId]: updated }));
      }
      return;
    }

    try {
      const valores = await databaseService.listItems<any>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
        { checklist_id: rowId },
      );
      const detalhados: DetailItem[] = [];
      for (const val of valores) {
        if (!val.valor) continue;
        let campoNome = '';
        let campoTipo = '';
        let campoGrupo = '';
        let campoOrdem = 0;
        if (val.campo_id) {
          const campo = await databaseService.readItem<any>(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
            val.campo_id,
          );
          campoNome = campo?.nome || '';
          campoTipo = campo?.tipo || '';
          campoGrupo = campo?.grupo || '';
          campoOrdem = campo?.ordem ?? 0;
        }

        let thumb: string | undefined;
        if (includeThumbs && IMAGE_FIELD_TYPES.includes(campoTipo)) {
          const valueStr = String(val.valor);
          const localTable = getLocalImageTableName(valueStr);
          if (localTable) {
            try {
              const row = await databaseService.readItem<{ base64?: string }>(localTable, valueStr);
              if (row?.base64) {
                thumb = resolveLocalImageDataUrl(row.base64, valueStr);
              }
            } catch (error) {
            }
          } else {
            thumb = makeThumb(valueStr);
            log.info('🔗 Consulta de rede para thumb', { campo: campoNome, valor: valueStr, url: thumb });
          }
        }

        detalhados.push({ campo: campoNome, valor: String(val.valor), tipo: campoTipo, thumb, grupo: campoGrupo, ordem: campoOrdem });
      }
      setDetailsCache((prev) => {
        if (!includeThumbs && prev[rowId]) {
          return prev;
        }
        return { ...prev, [rowId]: detalhados };
      });
    } catch (err) {
      log.error('Erro carregando detalhes checklist', rowId, err);
    }
  };

  const openRows = openRowsProp ?? openRowsState;
  const updateOpenRows = (rows: string[]) => {
    if (onOpenRowsChange) {
      onOpenRowsChange(rows);
      return;
    }
    setOpenRowsState(rows);
  };

  const toggleRow = async (rowId: string) => {
    if (openRows.includes(rowId)) {
      updateOpenRows(openRows.filter((id) => id !== rowId));
      return;
    }
    updateOpenRows([...openRows, rowId]);
    await fetchDetails(rowId, true);
  };

  // Prefetch detalhes para busca assim que novos dados chegarem
  useEffect(() => {
    data.forEach((row) => {
      if (!detailsCache[row.id]) {
        fetchDetails(row.id, false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!openRows.length) {
      return;
    }
    openRows.forEach((rowId) => {
      fetchDetails(rowId, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRows]);

  useEffect(() => {
    if (refreshToken === undefined) {
      return;
    }
    setDetailsCache({});
    if (!openRows.length) {
      return;
    }
    setTimeout(() => {
      openRows.forEach((rowId) => {
        fetchDetails(rowId, true, { force: true });
      });
    }, 0);
  }, [refreshToken]);

  const filteredData = data.filter((r) => {
    const term = search.toLowerCase();
    if (!term) return true;
    const sequential = getSequentialLabel(r).toLowerCase();
    const basicMatch =
      r.templateName.toLowerCase().includes(term) ||
      r.userName.toLowerCase().includes(term) ||
      statusLabel(r.status).toLowerCase().includes(term) ||
      sequential.includes(term);
    if (basicMatch) return true;
    const details = detailsCache[r.id];
    if (details) {
      return details.some((d) => d.valor.toLowerCase().includes(term) || d.campo.toLowerCase().includes(term));
    }
    return false;
  });

  // Loga resultados da pesquisa de forma estruturada
  useEffect(() => {
    if (!search) return;
    const structured = filteredData.map((row) => {
      const rowMatches = terms.filter((t) => {
        const sequential = getSequentialLabel(row).toLowerCase();
        const basicMatch =
          row.templateName.toLowerCase().includes(t) ||
          row.userName.toLowerCase().includes(t) ||
          statusLabel(row.status).toLowerCase().includes(t) ||
          sequential.includes(t);
        const detailMatch = detailsCache[row.id]?.some(
          (d) => d.valor.toLowerCase().includes(t) || d.campo.toLowerCase().includes(t),
        );
        return basicMatch || detailMatch;
      });
      return { checklistId: row.id, template: row.templateName, matchedTerms: rowMatches };
    });
    log.info('🔍 Resultados da busca', { termos: terms, resultados: structured });
  }, [search, filteredData, detailsCache]);

  useEffect(() => {
    log.info('📊 Render ChecklistsTableWeb', { rows: data.length, loading });
  }, [data.length, loading]);

  // ===== Seleção múltipla =====
  const allSelected = filteredData.length > 0 && filteredData.every((r) => selectedIds.has(r.id));

  const handleToggleSelectAll = () => {
    if (allSelected) {
      selectAll([]);
    } else {
      selectAll(filteredData.map((r) => r.id));
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingRight: theme.spacing.md,
        background: theme.colors.background.primary,
        borderRadius: theme.borderRadius.md,
        ...theme.shadows.sm,
      }}
    >
      {/* Barra de pesquisa */}
      <div style={{ padding: 8 }}>
        <input
          type="text"
          placeholder="Pesquisar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: 8,
          borderRadius: theme.borderRadius.md,
          border: `1px solid ${theme.colors.border.light}`,
        }}
      />
    </div>
    <div
      style={{
        flex: 1,
        overflowX: 'auto',
        overflowY: 'auto',
        paddingBottom: theme.spacing.md,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: 600,
        }}
      >
        <thead style={theadStyle as React.CSSProperties}>
          <tr>
            <th style={{ ...thStyle, width: 32 }}>
              <span
                role="checkbox"
                aria-checked={allSelected}
                style={{ cursor: 'pointer' }}
                onClick={handleToggleSelectAll}
              >
                <MaterialCommunityIcons
                  name={allSelected ? 'checkbox-marked' : 'checkbox-blank-outline'}
                  size={20}
                  color={theme.colors.text.inverse}
                />
              </span>
            </th>
            <th style={{ ...thStyle, width: 32 }} />
            <th style={thStyle}>Data</th>
            <th style={thStyle}>Checklist / Usuário</th>
            <th style={thStyle}>Status</th>
            <th style={{ ...thStyle, width: 84, textAlign: 'center' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((row, idx) => (
            <React.Fragment key={row.id}>
              <tr
                style={{
                  background: getRowBackground(idx),
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = theme.colors.background.secondary;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = getRowBackground(idx);
                }}
                onClick={() => {
                  if (selectedIds.size > 0) {
                    toggleSelect(row.id);
                  } else {
                    toggleRow(row.id);
                  }
                }}
              >
                {/* Checkbox */}
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span
                    role="checkbox"
                    aria-checked={selectedIds.has(row.id)}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(row.id);
                    }}
                  >
                    <MaterialCommunityIcons
                      name={selectedIds.has(row.id) ? 'checkbox-marked' : 'checkbox-blank-outline'}
                      size={20}
                      color={theme.colors.primary.default}
                    />
                  </span>
                </td>
                {/* Chevron */}
                <td style={{ ...tdStyle, textAlign: 'center', cursor: 'pointer' }}>
                  {/* Ícone de seta elegante, rotaciona ao expandir */}
                  <span
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                      transform: openRows.includes(row.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                      color: openRows.includes(row.id)
                        ? theme.colors.primary.default
                        : theme.colors.text.secondary,
                      verticalAlign: 'middle',
                    }}
                  >
                    <MaterialCommunityIcons
                      name="chevron-right"
                      size={22}
                      color={openRows.includes(row.id)
                        ? theme.colors.primary.default
                        : theme.colors.text.secondary}
                    />
                  </span>
                </td>
                <td style={tdStyle}>{formatDateTime(row.data_criacao)}</td>
                <td style={tdStyle}>
                  <div
                    style={{
                      color:
                        row.sequencial_codigo && row.sequencial_codigo.trim().length > 0
                          ? theme.colors.text.primary
                          : theme.colors.text.secondary,
                      fontSize: theme.fontSizes.sm,
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                    {highlightTerm(`Número: ${getSequentialLabel(row)}`)}
                  </div>
                  <div style={{ fontWeight: 600 }}>{highlightTerm(row.templateName)}</div>
                  <div style={{ color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm }}>
                    {highlightTerm(row.userName)}
                  </div>
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
                      borderRadius: theme.borderRadius.md,
                      background: statusColor(row.status),
                      color: theme.colors.white,
                      fontSize: theme.fontSizes.xs,
                      fontWeight: 600,
                    }}
                  >
                    {statusLabel(row.status)}
                  </span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push({
                        pathname: '/(app)/checklist/[id]',
                        params: { id: row.id, returnTo: '/(app)/admin/dashboard' },
                      });
                    }}
                    disabled={selectedIds.size > 0}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: `1px solid ${theme.colors.border.light}`,
                      background: selectedIds.size > 0 ? theme.colors.background.tertiary : theme.colors.background.primary,
                      color: theme.colors.text.primary,
                      cursor: selectedIds.size > 0 ? 'not-allowed' : 'pointer',
                    }}
                    aria-label={`Editar checklist ${row.id}`}
                    title={selectedIds.size > 0 ? 'Finalize a seleção para editar' : 'Editar checklist'}
                  >
                    <MaterialCommunityIcons
                      name="pencil-outline"
                      size={18}
                      color={selectedIds.size > 0 ? theme.colors.text.tertiary : theme.colors.primary.default}
                    />
                    <span style={{ fontSize: theme.fontSizes.xs, fontWeight: 600 }}>Editar</span>
                  </button>
                </td>
              </tr>
              {openRows.includes(row.id) && (
                <tr>
                  <td colSpan={5} style={{ padding: '4px 12px', background: theme.colors.background.tertiary }}>
                    {detailsCache[row.id] ? (
                      detailsCache[row.id].length ? (
                        (() => {
                          const details = detailsCache[row.id];
                          // Agrupa por grupo
                          const groups: Record<string, DetailItem[]> = {};
                          details.forEach((d) => {
                            const g = d.grupo || '';
                            if (!groups[g]) groups[g] = [];
                            groups[g].push(d);
                          });
                          // Organiza grupos pela menor ordem encontrada
                          const orderedGroups = Object.entries(groups).sort((a, b) => {
                            const orderA = a[1][0]?.ordem ?? 0;
                            const orderB = b[1][0]?.ordem ?? 0;
                            return orderA - orderB;
                          });
                          return orderedGroups.map(([groupName, items]) => (
                            <div key={groupName} style={{ marginBottom: 8 }}>
                              {groupName ? (
                                <div style={{ fontWeight: 600, marginBottom: 4, color: theme.colors.text.primary }}>
                                  {groupName}
                                </div>
                              ) : null}
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {items.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)).map((d, i) => (
                                  <li key={i} style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.primary }}>
                                    {d.tipo === 'mensagem pronta' ? (
                                      <span style={{ fontWeight: 'bold' }}>{highlightTerm(d.valor)}</span>
                                    ) : (
                                      <>
                                        {d.campo ? <span style={{ fontWeight: 'bold' }}>{highlightTerm(d.campo)}: </span> : null}
                                        {IMAGE_FIELD_TYPES.includes(d.tipo) && isStorageMediaReference(d.valor) ? (
                                          <PartnerMediaThumbnail value={d.valor} checklistId={row.id} />
                                        ) : IMAGE_FIELD_TYPES.includes(d.tipo) && d.thumb ? (
                                          <img
                                            src={d.thumb}
                                            alt={d.campo}
                                            style={{ maxWidth: 60, maxHeight: 60, marginRight: 6, verticalAlign: 'middle' }}
                                          />
                                        ) : null}
                                        {IMAGE_FIELD_TYPES.includes(d.tipo) ? null : highlightTerm(d.valor)}
                                      </>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ));
                        })()
                      ) : (
                        <span style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary }}>
                          Nenhum valor de texto.
                        </span>
                      )
                    ) : (
                      <span style={{ fontSize: theme.fontSizes.sm }}>Carregando...</span>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {loading && (
            <tr>
              <td colSpan={5} style={{ padding: 16, textAlign: 'center' }}>
                Carregando...
              </td>
            </tr>
          )}
          {!loading && onLoadMore && hasMore && (
            <tr>
              <td colSpan={5} style={{ padding: 16, textAlign: 'center' }}>
                <button
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: theme.colors.primary.default,
                    color: theme.colors.text.inverse,
                    cursor: 'pointer',
                  }}
                  onClick={onLoadMore}
                >
                  Carregar mais ({totalCount - data.length} restantes)
                </button>
                <div style={{ marginTop: 8, fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>
                  Mostrando {data.length} de {totalCount}
                </div>
              </td>
            </tr>
          )}
          {!loading && !hasMore && (
            <tr>
              <td colSpan={5} style={{ padding: 16, textAlign: 'center', fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>
                Mostrando todos os {totalCount} registros
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  backgroundColor: theme.colors.primary.default,
  color: theme.colors.text.inverse,
  fontWeight: 600,
};

const theadStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.card,
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  color: theme.colors.text.primary,
};

// Helper para formatar data e hora com separador " - "
function formatDateTime(ts: number) {
  const d = new Date(ts);
  return `${d.toLocaleDateString('pt-BR')} - ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
} 
